import React, {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import {
  BackendHealthIndicator as BackendHealthIndicatorView,
  type BackendHealthIndicatorProps,
} from './BackendHealthIndicator';
import { useBackendHealthIndicatorData } from './BackendHealthIndicatorHOC';

export type { BackendHealthIndicatorProps };

type BackendHealthStatusValue = {
  panelProps: BackendHealthIndicatorProps | null;
};

const BackendHealthStatusContext =
  createContext<BackendHealthStatusValue | null>(null);

/**
 * Runs {@link useBackendHealthIndicatorData} once per subtree so readiness props
 * feed {@link BackendHealthIndicator} (floating panel) and {@link OfflineIndicator}
 * (viewport ring for combined offline mode: browser or backend readiness failure).
 */
export function BackendHealthStatusProvider({
  children,
}: {
  children: ReactNode;
}) {
  const panelProps = useBackendHealthIndicatorData();
  const value = useMemo(() => ({ panelProps }), [panelProps]);
  return (
    <BackendHealthStatusContext.Provider value={value}>
      {children}
    </BackendHealthStatusContext.Provider>
  );
}

export function useBackendHealthStatus(): BackendHealthStatusValue {
  return (
    useContext(BackendHealthStatusContext) ?? { panelProps: null }
  );
}

/**
 * Dev-only floating panel driven solely by backend readiness (checking, unhealthy,
 * or failed). Independent of `navigator.onLine` — browser connectivity is only
 * reflected by {@link OfflineIndicator} (viewport ring).
 */
export const BackendHealthIndicator: React.FC = () => {
  const { panelProps } = useBackendHealthStatus();
  if (!panelProps) {
    return null;
  }
  return <BackendHealthIndicatorView {...panelProps} />;
};
BackendHealthIndicator.displayName = 'BackendHealthIndicator';
